/**
 * Shared building blocks for the Administration console (M2 W5b). Keeps the five admin
 * screens consistent with the rest of the app: the same PageHeader -> content -> PageFooter
 * shell, a permission GUARD that renders a friendly "not authorized" state on a deep-link
 * (defense in depth on top of the server RBAC + the gated nav), and small presentational
 * helpers (status badge, scope label). No PHI here (operator + registry data only).
 */
import type { ReactNode } from "react";
import { ShieldAlert } from "lucide-react";
import { SkyBadge, SkyMessageBar, type SkyBadgeColor } from "@skypointcloud/skypoint-ui";
import { PageHeader, type Crumb } from "../../shell/PageHeader";
import { PageFooter } from "../../shell/PageFooter";
import { usePermission } from "../../lib/session";

/** Map an entity status to a semantic badge color (status by color + LABEL, never brand amber). */
export function statusColor(status: string): SkyBadgeColor {
  switch (status.toLowerCase()) {
    case "active":
      return "success";
    case "pending":
    case "provisioning":
      return "warning";
    case "blocked":
    case "disabled":
    case "inactive":
      return "danger";
    default:
      return "subtle";
  }
}

/** A status pill: color + the literal status text (accessible, never color-only). */
export function StatusBadge({ status }: { status: string }) {
  return (
    <SkyBadge appearance="tint" color={statusColor(status)}>
      {status}
    </SkyBadge>
  );
}

/** Human label for a role-assignment scope (global > tenant > instance). */
export function scopeLabel(tenantId: string | null, instanceId: string | null): string {
  if (instanceId) return "Instance";
  if (tenantId) return "Tenant";
  return "Global";
}

/**
 * Admin page shell. Renders the locked PageHeader + footer around `children`. When the
 * principal lacks EVERY one of `requires` (OR semantics, admin bypass via usePermission),
 * the work area is replaced with a not-authorized message bar instead of the content, so a
 * non-admin who deep-links to /admin/* gets a clear, non-leaky 403-style state.
 */
export function AdminPage({
  crumbs,
  title,
  description,
  requires,
  headerAction,
  children,
}: {
  crumbs: Crumb[];
  title: string;
  description?: ReactNode;
  requires: string[];
  headerAction?: ReactNode;
  children: ReactNode;
}) {
  const { hasPermission } = usePermission();
  const authorized = requires.some((p) => hasPermission(p));

  return (
    <>
      <PageHeader
        crumbs={crumbs}
        title={title}
        description={description}
        headerAction={authorized ? headerAction : undefined}
      />
      <section className="mt-6 flex flex-1 flex-col gap-3">
        {authorized ? (
          children
        ) : (
          <SkyMessageBar
            intent="error"
            icon={ShieldAlert}
            title="Not authorized"
            message="You do not have permission to view this administration screen. Contact your administrator if you believe this is a mistake."
          />
        )}
      </section>
      <PageFooter />
    </>
  );
}

/** Standard inline error bar for a problem+json failure (title only, never a stack/PHI). */
export function AdminError({ title, message }: { title: string; message: string }) {
  return <SkyMessageBar intent="error" title={title} message={message} />;
}
